import React, {useState} from 'react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import type {MultidomainProductBadge, Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {longWordsLanguages, getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {FeedbackStar} from '~/icons/feedback-star'
import {AmazonLocalizedLogo} from '~/icons/icon-amazon-logo-by-locale'
import {ReviewsIcon} from '~/icons/reviews-icon'
import {AmazonLogoLetterIcon} from '~/icons/amazon-logo-letter-icon'
import type {DomainConfig} from '~/domains'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {MadeInByLocaleIcon} from '~/icons/made-in-by-locale-icon'
import {ChoiceOfByLocaleBadge} from '~/icons/choice-of-by-locale-badge'
import {CompareIcon} from '~/icons/compare-icon'
import {DiscountBadge} from '../badges/discount-badge'
import ProductCardBadge from '../badges/card-placement-badge'
import UserValueEUBadge from '../badges/user-value-eu-layout-badge'
import {DiscountTextInfoBadge} from '../badges/discount-text-info-badge'
import {MagaLoclaedBrandBadge} from '../badges/localed-brand-badge'
import SaleEventBadge from '../badges/saleEventBadge'
import KeyFeaturesEULayout from './key-features-eu-layout'

export interface ProductCardProps {
  index: number
  product: Product
}

function getBadgeContentOrder(
  domainType: DomainConfig['domainType'],
  locale: string,
): Record<number, MultidomainProductBadge> {
  switch (true) {
    case domainType === 'maga' && locale === 'ca':
      return {
        0: 'countryPick',
        1: 'countryTopRated',
        2: 'countryFavorite',
      }
    case domainType === 'maga' && locale === 'us':
      return {
        0: 'countryTopRated',
      }
    default:
      return {
        0: 'bestChoice',
        1: 'valueForMoney',
      }
  }
}
export const ProductEUCard: FunctionComponent<ProductCardProps> = ({product, index}) => {
  const {
    image,
    name: initialName,
    nameTranslation,
    brand = '',
    listScore,
    affiliateLink,
    discount,
    numOfRatings,
    coupon,
    isSellerProduct,
    asin,
    price,
    chosenBy,
    productPageSlug,
    forcedPosition,
    isLocaleBrand,
  } = product

  const {
    locale,
    language,
    forcedLanguage,
    featureFlags,
    trackAnalytics,
    domain,
    currencyCode,
    hostname,
    renderTimestamp,
  } = useRouteLoaderData('root') as RootLoaderResponse

  const productName = (forcedLanguage ? nameTranslation : initialName) ?? initialName
  const name = productName?.replace(/[|/]/g, ' ')

  const {
    dataResponse: {productsCategory} = {} as SlugApiResponse,
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {domainType, styles: stylesBydomain} = getDomainConfig(domain, locale)
  const [isKeyFeaturesOpen, setIsKeyFeaturesOpen] = useState(false)

  const isMagaDomainType = domainType === 'maga'
  const isAdditionalProductCard = index > 9

  const showChoiceOfBadge = index === 0 && isMagaDomainType
  const showIsLocaledBrandIcon = !showChoiceOfBadge && isLocaleBrand && isMagaDomainType
  const showIsLocaledBrandBadge = isLocaleBrand && isMagaDomainType

  const isDiscountExist = Boolean(discount)

  const showKeyFeatures =
    product?.keyFeatures &&
    product?.keyFeatures?.length > 0 &&
    forcedLanguage?.toLowerCase() !== 'es' &&
    !isAdditionalProductCard

  const showVariantsIndication =
    featureFlags?.showVariantsIndication && product?.variationDetails && product?.variationDetails.length > 0

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )
  const showCompareButton = locale?.toLowerCase() === 'de' && index < 3

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  const displayScore = listScore
  const countFormater = new Intl.NumberFormat('fr-FR')
  const reviewsCount = countFormater.format(numOfRatings ?? 0)
  const chosenByFormatted = countFormater.format(chosenBy ?? 0)

  const reportWidgetClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickout({
    product,
    index: index + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })

  const [cardRef] = useReportItemSeen({
    item_list_id: productsCategory?.slug,
    item_name: name,
    item_source: 'list',
    partner_item_brand: brand,
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: isSellerProduct,
    price: price?.amount,
    currency: price?.currency,
    click_element_name: 'category-product',
    index: index + 1,
    variation: JSON.stringify({
      hasVariantIndication: showVariantsIndication,
      forcedPosition,
    }),
  })

  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'product-card',
      widget_place: 'list',
      variation: JSON.stringify({
        hasVariantIndication: showVariantsIndication,
        forcedPosition,
      }),
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: name,
            image: [image],
            description: name,
            brand: {
              '@type': 'Brand',
              name: brand,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price?.amount,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: brand ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>

      <div
        data-testid={`product-card-${index}`}
        ref={cardRef}
        className="border-gray relative z-20 flex h-full min-h-52 w-full flex-col overflow-hidden rounded-2xl border bg-white shadow-md transition-all duration-200 ease-in-out"
      >
        {showIsLocaledBrandIcon && (
          <MadeInByLocaleIcon
            className="absolute -top-2 right-3 size-20 rotate-12 drop-shadow-[2px_4px_6px_rgba(0,0,0,0.4)] tablet:right-2"
            locale={locale}
          />
        )}
        {showChoiceOfBadge && (
          <ChoiceOfByLocaleBadge
            className="absolute -top-2 right-3 size-20 rotate-12 drop-shadow-[2px_4px_6px_rgba(0,0,0,0.4)] tablet:right-2"
            locale={locale}
          />
        )}
        {!isAdditionalProductCard ? (
          <div className="flex flex-row gap-2 px-6">
            <ProductCardBadge index={index} coupon={coupon} className="overflow-hidden rounded-b-lg rounded-t-none" />
            <UserValueEUBadge
              badge={getBadgeContentOrder(domainType, locale.toLowerCase())[index]}
              className={clsx(
                'w-fit overflow-hidden rounded-b-lg rounded-t-none',
                stylesBydomain.LISTPAGE_USER_VALUE_BADGE,
                'listpage_user_value_badge',
              )}
            />
          </div>
        ) : null}
        <div className={clsx('px-10 pb-6 tablet:px-2 tablet:pb-4', isAdditionalProductCard ? 'pt-6' : 'pt-1')}>
          <div className="flex flex-row gap-6 tablet:flex-col tablet:gap-1 tablet:px-4">
            {/* -------------------------------------- IMAGE------------------------------- */}
            <div className="relative flex size-44 shrink-0 flex-col items-center justify-center tablet:size-auto tablet:flex-row tablet:justify-evenly tablet:gap-5">
              <img
                src={image}
                loading={index > 2 ? 'lazy' : 'eager'}
                alt={name?.slice(0, 10)}
                className="relative size-44 shrink-0 grow-0 object-scale-down mix-blend-multiply tablet:size-32"
              />
              <a
                href={redirectUrl}
                onClick={handleCardClick}
                aria-label={`view ${brand}`}
                target="_blank"
                rel="sponsored noreferrer"
                className="absolute inset-0"
              />
              {/* -------------------------------------- Rating/Reviews Mobile------------------------------- */}
              {isAdditionalProductCard && numOfRatings && numOfRatings > 0 ? (
                <div className="hidden flex-row items-center justify-evenly tablet:flex">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-center gap-2 text-2xl font-normal text-gray-900">
                      {reviewsCount} <ReviewsIcon />
                    </div>
                    <span className="w-20 text-sm text-gray-500">{translations.ProductCard.verifiedReviews}</span>
                  </div>
                </div>
              ) : null}
              {!isAdditionalProductCard ? (
                <div className="hidden flex-row items-center justify-evenly tablet:flex">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-center gap-2 text-3xl font-medium text-gray-900">
                      {scoreFormatter.format(displayScore)} <FeedbackStar className="size-4 fill-[#FF9900]" />
                    </div>
                    {numOfRatings && numOfRatings > 0 ? (
                      <span className="text-sm text-gray-500">
                        {translations.ProductCard.reviewsFormat(reviewsCount)}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
            {/* -------------------------------------- INFO------------------------------- */}
            <div className="flex flex-col justify-center gap-3">
              <div className="flex flex-row items-center gap-x-4 gap-y-2 *:-mb-1 tablet:flex-wrap">
                <span className="relative text-base font-bold capitalize text-gray-900">
                  {brand}
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className="absolute inset-0"
                  />
                </span>
                {showIsLocaledBrandBadge && (
                  <MagaLoclaedBrandBadge className="[&>div>svg>g>g>.border-flag]:!stroke-none" />
                )}
              </div>
              {/* ------------------------------------- Name ------------------------------- */}

              <h2 className="relative line-clamp-2 overflow-hidden text-base font-light leading-6 no-underline">
                {name}
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className="absolute inset-0"
                />
              </h2>
              {/* ----------------------------------- Discount & Delivery Badges-------------------- */}
              <div className="relative flex flex-row gap-2">
                {isDiscountExist ? (
                  <DiscountBadge
                    discount={discount}
                    className={clsx(
                      'flex items-center rounded-lg px-2 py-1 !text-sm lowercase',
                      stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                      stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                    )}
                  />
                ) : null}
                <div className="rounded-lg bg-[#DDF1FE] px-2 py-1 text-sm text-black">
                  {translations.Badge.fastDelivery}
                </div>
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                  className="absolute inset-0"
                />
              </div>
            </div>
            <div
              className={clsx(
                'ml-auto flex flex-col desktop:justify-center tablet:mx-auto tablet:w-full',
                isMagaDomainType ? 'w-71 min-w-71' : 'w-56 min-w-56',
              )}
            >
              {/* -------------------------------------- Rating/Reviews Desktop------------------------------- */}
              {isAdditionalProductCard ? (
                <div className="relative flex flex-row items-center justify-evenly tablet:hidden">
                  {numOfRatings && numOfRatings > 0 ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-center gap-2 text-2xl font-normal text-gray-900">
                        {reviewsCount} <ReviewsIcon />
                      </div>
                      <span className="w-20 text-sm text-gray-500">{translations.ProductCard.verifiedReviews}</span>
                    </div>
                  ) : null}
                  <AmazonLocalizedLogo locale={locale?.toLowerCase()} />
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className="absolute inset-0"
                  />
                </div>
              ) : (
                <div className="relative flex flex-row items-center justify-evenly tablet:hidden">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-center gap-2 text-3xl font-medium text-gray-900">
                      {scoreFormatter.format(displayScore)} <FeedbackStar className="size-4 fill-[#FF9900]" />
                    </div>
                    {numOfRatings && numOfRatings > 0 ? (
                      <span className="text-sm text-gray-500">
                        {translations.ProductCard.reviewsFormat(reviewsCount)}
                      </span>
                    ) : null}
                  </div>
                  <AmazonLocalizedLogo locale={locale?.toLowerCase()} />
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className="absolute inset-0"
                  />
                </div>
              )}
              {/* -------------------------------------------CTA---------------------------------------- */}
              <div
                className={clsx(
                  'mt-2 flex w-full flex-col items-center justify-center tablet:mt-5 tablet:gap-x-4 tablet:gap-y-2',
                  !isMagaDomainType && 'tablet:flex-row',
                )}
              >
                {!isMagaDomainType && (
                  <AmazonLocalizedLogo locale={locale?.toLowerCase()} className="hidden shrink-0 tablet:block" />
                )}

                <div className={clsx('flex w-full flex-col gap-3', isMagaDomainType ? 'mt-auto' : 'mt-6 tablet:mt-0')}>
                  {isMagaDomainType && (
                    <DiscountTextInfoBadge
                      className="mt-4 w-full [&>div]:rounded-lg [&>div]:px-2 [&>div]:py-1.5"
                      haveDiscount={Boolean(discount)}
                    />
                  )}
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className={clsx(
                      'flex h-10 w-full cursor-pointer items-center justify-center rounded-3xl text-center capitalize',
                      longWordsLanguages.includes(language) ? 'px-1 text-base desktop:text-sm' : 'text-base',
                      stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                      'listpage_button_primary',
                    )}
                  >
                    {isMagaDomainType && <AmazonLogoLetterIcon className="mr-2 *:fill-white *:!stroke-none" />}
                    {translations.ProductCard.additionalCtaText}
                  </a>
                </div>
              </div>

              {showCompareButton && (
                <a
                  href="#compare-top-picks"
                  onClick={() => {
                    reportWidgetClick({
                      widget_name: 'compare-button',
                      widget_place: `card-${index + 1}`,
                    })
                  }}
                  className={clsx(
                    'mx-auto mb-1 mt-4 flex flex-row items-center gap-3 uppercase',
                    stylesBydomain.LISTPAGE_BUTTON_LINK,
                    'listpage_button_link',
                  )}
                >
                  <CompareIcon
                    className={clsx(stylesBydomain.LISTPAGE_BUTTON_ICON_FILL, 'listpage_button_icon_fill')}
                  />
                  {translations.ProductCard.compare}
                </a>
              )}
              {chosenBy && chosenBy > 0 ? (
                <span className="mx-auto mt-2 text-sm text-gray-500">
                  {translations.ProductCard.buyersCount(chosenByFormatted)}
                </span>
              ) : null}
              {!!discount && (
                <SaleEventBadge
                  redirectUrl={redirectUrl}
                  handleCardClick={handleCardClick}
                  brand={brand}
                  index={index}
                  placement="eu-card"
                />
              )}
            </div>
          </div>
        </div>
        {showKeyFeatures ? (
          <>
            <div className="border-b border-solid" />
            <KeyFeaturesEULayout
              placement={index + 1}
              productPageSlug={productPageSlug ?? ''}
              handleCardClick={handleCardClick}
              product={product}
              redirectUrl={redirectUrl}
              isKeyFeaturesOpen={isKeyFeaturesOpen}
              setIsKeyFeaturesOpen={setIsKeyFeaturesOpen}
            />
          </>
        ) : null}
      </div>
    </>
  )
}
